import { Injectable, inject } from "@angular/core";
import type { ScopedProject } from "@domain/project/project-guards";
import { isMonitoringProject } from "@domain/project/project-guards";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { HalfPageContainerService } from "@pdf/helpers/container/half-page/half-page-container.service";
import { DraegerFonts } from "@pdf/helpers/draeger-fonts";
import { ContainerProperties, FontNames, Headlines, PdfProperties, TableProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { Observable } from "rxjs";

type ContentType = "paragraph" | "listIntro" | "listItem";

interface TextBlock {
  type: ContentType;
  text: string;
}

const LIST_BULLET = "\u2022"; // bullet •
const LIST_BULLET_X_OFFSET = 12; // bullet indented 12px from normal text left edge
const LIST_TEXT_X_OFFSET = 24; // text starts 12px to the right of the bullet (12 + 12)

@Injectable({
  providedIn: "root",
})
export class SignOffPagePdfService {
  private containerService = inject(HalfPageContainerService);
  private headlines = new Headlines();

  generate(pdf: jsPDF, tableOfContents: TableOfContents, project: ScopedProject): Observable<jsPDF> {
    return new Observable((observer) => {
      this.addFonts(pdf);
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      const container = new HalfPageContainer();
      this.addSignOffPage(pdf, container, tableOfContents, project);
      observer.next(pdf);
      observer.complete();
    });
  }

  private addFonts(doc: jsPDF) {
    new DraegerFonts().fonts.forEach((font) => {
      doc.addFileToVFS(font.ttfName, font.base64);
      doc.addFont(font.ttfName, font.fontName, font.fontWeight);
    });
  }

  private addSignOffPage(pdf: jsPDF, container: HalfPageContainer, tableOfContents: TableOfContents, project: ScopedProject) {
    const pageHeadline = this.headlines.ACCEPTANCE;
    this.containerService.addOnNewPage(pdf, container, pageHeadline, tableOfContents);
    container.yPosition += TableProperties.HEADLINE_OFFSET_Y;

    const textWidth = ContainerProperties.WIDTH - 2 * TableProperties.PADDING_X;
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT, "normal");

    const blocks = this.getContent();
    blocks.forEach((block, index) => {
      const isLast = index === blocks.length - 1;
      const nextBlock = blocks[index + 1];
      this.renderBlock(pdf, container, block, nextBlock, isLast, pageHeadline, tableOfContents, textWidth);
    });

    this.addSignatureSection(pdf, container, project);
  }

  private renderBlock(
    pdf: jsPDF,
    container: HalfPageContainer,
    block: TextBlock,
    nextBlock: TextBlock | undefined,
    isLastBlock: boolean,
    pageHeadline: string,
    tableOfContents: TableOfContents,
    textWidth: number,
  ) {
    pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT, "normal");

    const isListItem = block.type === "listItem";
    // For list items: bullet at LIST_BULLET_X_OFFSET, text at LIST_TEXT_X_OFFSET (both from PADDING_X)
    const textXOffset = TableProperties.PADDING_X + (isListItem ? LIST_TEXT_X_OFFSET : 0);
    const wrapWidth = textWidth - (isListItem ? LIST_TEXT_X_OFFSET : 0);

    const lines = pdf.splitTextToSize(block.text, wrapWidth);
    const lineHeight = pdf.getTextDimensions("A").h * PdfProperties.POINT_TO_PIXEL_RATIO;
    const textHeight = lines.length * lineHeight;
    const gapAfter =
      block.type === "listIntro" || (block.type === "listItem" && nextBlock?.type === "listItem")
        ? 0
        : TableProperties.PADDING_26;
    const totalHeight = textHeight + gapAfter;

    const remainingSpace = ContainerProperties.Y + ContainerProperties.HEIGHT - container.yPosition;
    if (remainingSpace < totalHeight) {
      this.containerService.add(pdf, container, pageHeadline, tableOfContents);
      container.yPosition += TableProperties.HEADLINE_OFFSET_Y;
      pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT, "normal");
    }

    if (isListItem) {
      pdf.text(LIST_BULLET, container.xPosition + TableProperties.PADDING_X + LIST_BULLET_X_OFFSET, container.yPosition, {
        baseline: "top",
      });
    }
    pdf.text(lines, container.xPosition + textXOffset, container.yPosition, { baseline: "top" });
    container.yPosition += isLastBlock ? textHeight : totalHeight;
  }

  private addSignatureSection(pdf: jsPDF, container: HalfPageContainer, project: ScopedProject) {
    const SECTION_GAP = 128; // gap above signature section
    const HEADLINE_TO_LINE = 160; // gap between bottom of headline and the first line
    const LABEL_TO_LINE = 116; // gap between bottom of label text and the next line
    const LINE_TO_LABEL = 12; // gap between the line and the label text below it
    const PREFILL_FONT_SIZE = TableProperties.HEADER_FONT_SIZE; // slightly larger than body text
    const PREFILL_TO_LINE_GAP = 20; // gap between prefill text and the line above it
    const COLUMN_GAP = 64;
    const SIG_LINE_WIDTH = 1;
    const LINE_RIGHT_PADDING = 72; // padding on the right side of each signature line

    const totalWidth = ContainerProperties.WIDTH - 2 * TableProperties.PADDING_X;
    const colWidth = (totalWidth - COLUMN_GAP) / 2;
    const col1X = container.xPosition + TableProperties.PADDING_X;
    const col2X = col1X + colWidth + COLUMN_GAP;

    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    const labelHeight = pdf.getTextDimensions("A").h * PdfProperties.POINT_TO_PIXEL_RATIO;

    const customerName = [project.contactPerson.firstname, project.contactPerson.lastname].filter(Boolean).join(" ");
    const draegerName = isMonitoringProject(project) ? (project.draegerContactPerson ?? "") : "";

    const columnDefs = [
      {
        x: col1X,
        headline: $localize`:@@monitoring.pdfExport.signOff.customerHeadline:Customer Signature by authorised representative:`,
        prefillName: customerName,
      },
      {
        x: col2X,
        headline: $localize`:@@monitoring.pdfExport.signOff.draegerHeadline:Dr\u00e4ger Signature by authorised representative:`,
        prefillName: draegerName,
      },
    ];
    const labelNames = [
      $localize`:@@monitoring.pdfExport.signOff.signature:Signature`,
      $localize`:@@monitoring.pdfExport.signOff.name:Name`,
      $localize`:@@monitoring.pdfExport.signOff.position:Position`,
      $localize`:@@monitoring.pdfExport.signOff.date:Date`,
    ];

    for (const colDef of columnDefs) {
      let y = container.yPosition + SECTION_GAP;

      // Headline – semibold
      pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT_BOLD, "normal");
      const headlineLines = pdf.splitTextToSize(colDef.headline, colWidth);
      pdf.text(headlineLines, colDef.x, y, { baseline: "top" });
      y += headlineLines.length * labelHeight + HEADLINE_TO_LINE;

      // Lines with labels
      labelNames.forEach((label, index) => {
        const isNameRow = index === 1;

        // Prefill name above the line, left-aligned, in a slightly larger font
        if (isNameRow && colDef.prefillName) {
          pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT, "normal");
          pdf.setFontSize(PREFILL_FONT_SIZE);
          pdf.text(colDef.prefillName, colDef.x, y, { baseline: "top" });
          const prefillHeight = pdf.getTextDimensions("A").h * PdfProperties.POINT_TO_PIXEL_RATIO;
          y += prefillHeight + PREFILL_TO_LINE_GAP;
          pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
        }

        pdf.setLineWidth(SIG_LINE_WIDTH);
        pdf.setDrawColor(PdfProperties.DRAEGERBLUE);
        pdf.line(colDef.x, y, colDef.x + colWidth - LINE_RIGHT_PADDING, y);
        y += LINE_TO_LABEL;

        pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
        pdf.text(label, colDef.x, y, { baseline: "top" });

        if (index < labelNames.length - 1) {
          y += labelHeight + LABEL_TO_LINE;
        }
      });
    }
  }

  private getContent(): TextBlock[] {
    return [
      {
        type: "paragraph",
        text: $localize`:@@monitoring.pdfExport.acceptance.paragraph1:This document records the outcomes of the site survey conducted at the above location(s). The information captured reflects the site conditions, requirements, and constraints observed and discussed at the time of the survey.`,
      },
      {
        type: "paragraph",
        text: $localize`:@@monitoring.pdfExport.acceptance.paragraph2:This site survey sign\u2011off does not constitute final acceptance of any solution, system design, quotation, delivery, installation, or contract. Formal acceptance shall occur only through a separate written agreement executed by both parties.`,
      },
      {
        type: "listIntro",
        text: $localize`:@@monitoring.pdfExport.acceptance.bySigningBelow:By signing below:`,
      },
      {
        type: "listItem",
        text: $localize`:@@monitoring.pdfExport.acceptance.customerConfirms:The Customer confirms that the information relating to their site, infrastructure, workflows, and requirements is accurate to the best of their knowledge at the time of the survey.`,
      },
      {
        type: "listItem",
        text: $localize`:@@monitoring.pdfExport.acceptance.draegerConfirms:Dr\u00e4ger confirms that the site survey was conducted in accordance with standard practices and that the information documented reflects the findings observed during the site visit.`,
      },
      {
        type: "paragraph",
        text: $localize`:@@monitoring.pdfExport.acceptance.bothPartiesAcknowledge:Both parties acknowledge that this site survey forms the basis for solution design, configuration, quotation, and/or implementation planning. Any changes to site conditions, scope, infrastructure, or customer requirements following this survey may require a revised site survey and may impact the proposed solution, delivery timeline, or cost.`,
      },
    ];
  }
}
